import React from 'react';
import { Modal, Button } from 'react-bootstrap';

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const number = Number(value);
    if (Number.isNaN(number)) return 'N/A';
    return `${number.toLocaleString('en-US')} EGP`;
};

const formatDate = (value) => {
    if (!value) return 'N/A';
    return String(value).split('T')[0];
};

const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (Number.isNaN(number)) return null;
    return `${number}%`;
};

/** Commission labels always carry the percentage currently saved in the settings. */
const commissionLabel = (base, percent) => {
    const formatted = formatPercent(percent);
    return formatted ? `${base} (${formatted})` : base;
};

const DetailRow = ({ label, value }) => (
    <div className="col-sm-6">
        <div className="mb-3">
            <strong>{label}:</strong> {value === null || value === undefined || value === '' ? 'N/A' : value}
        </div>
    </div>
);

const DocumentsList = ({ title, documents }) => (
    <div className="mb-3">
        <strong>{title}:</strong>{' '}
        {documents.length === 0 ? (
            'N/A'
        ) : (
            <span className="d-inline-flex flex-wrap gap-2">
                {documents.map((document, index) => (
                    <a
                        key={document.id ?? index}
                        href={document.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#487fff', textDecoration: 'underline' }}
                    >
                        File {index + 1}
                    </a>
                ))}
            </span>
        )}
    </div>
);

const ViewResaleUnitModal = ({ show, handleClose, resaleUnit }) => {
    if (!resaleUnit) return null;

    const documents = resaleUnit.documents || [];
    const installmentPapers = documents.filter((document) => document.type === 'installment_paper');
    const purchaseDocuments = documents.filter((document) => document.type === 'purchase_document');
    const otherDocuments = documents.filter(
        (document) => document.type !== 'installment_paper' && document.type !== 'purchase_document'
    );
    const gallery = resaleUnit.gallery || [];

    return (
        <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Resale Unit #{resaleUnit.id}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h6 className="mb-3">Seller</h6>
                <div className="row">
                    <DetailRow label="Name" value={resaleUnit.sellerName} />
                    <DetailRow label="Mobile" value={resaleUnit.sellerMobile} />
                    <DetailRow label="Email" value={resaleUnit.sellerEmail} />
                    <DetailRow label="App account" value={resaleUnit.seller?.name} />
                </div>

                <hr />
                <h6 className="mb-3">Unit</h6>
                <div className="row">
                    <DetailRow label="Developer" value={resaleUnit.developerName} />
                    <DetailRow label="Project / Compound" value={resaleUnit.projectName} />
                    <DetailRow label="Type" value={resaleUnit.type} />
                    <DetailRow label="Location" value={resaleUnit.location} />
                    <DetailRow label="Area" value={resaleUnit.area ? `${resaleUnit.area} m²` : null} />
                    <DetailRow label="Finishing" value={resaleUnit.finishing} />
                    <DetailRow label="Bedrooms" value={resaleUnit.numberOfBedrooms} />
                    <DetailRow label="Bathrooms" value={resaleUnit.numberOfBathrooms} />
                    <DetailRow label="Unit Code" value={resaleUnit.unitCode} />
                    <DetailRow label="Delivery" value={formatDate(resaleUnit.deliveryDate)} />
                    <DetailRow label="Open to negotiation" value={resaleUnit.isNegotiable ? 'Yes' : 'No'} />
                    <DetailRow label="Interest requests" value={resaleUnit.interests_count ?? 0} />
                </div>

                <hr />
                <h6 className="mb-3">Financials (zero over-price)</h6>
                <div className="row">
                    <DetailRow label="Contract price" value={formatMoney(resaleUnit.contractPrice)} />
                    <DetailRow label="Paid so far (cash to seller)" value={formatMoney(resaleUnit.paidAmount)} />
                    <DetailRow label="Remaining to developer" value={formatMoney(resaleUnit.remainingAmount)} />
                    <DetailRow label="Installment" value={formatMoney(resaleUnit.installmentAmount)} />
                    <DetailRow label="Installment frequency" value={resaleUnit.installmentFrequency} />
                    <DetailRow label="Remaining installments" value={resaleUnit.remainingInstallments} />
                    <DetailRow label="Next installment" value={formatDate(resaleUnit.nextInstallmentDate)} />
                    <DetailRow label="Developer price today" value={formatMoney(resaleUnit.marketPriceToday)} />
                    <DetailRow
                        label={commissionLabel('Our commission on the buyer', resaleUnit.buyerCommissionPercent)}
                        value={formatMoney(resaleUnit.buyerCommissionAmount)}
                    />
                    <DetailRow label="Buyer total cash needed" value={formatMoney(resaleUnit.buyerTotalCashNeeded)} />
                    <DetailRow label="Buyer gain vs market" value={formatMoney(resaleUnit.buyerGain)} />
                    <DetailRow
                        label={commissionLabel('Our commission on the seller', resaleUnit.sellerCommissionPercent)}
                        value={formatMoney(resaleUnit.sellerCommissionAmount)}
                    />
                    <DetailRow label="Seller net payout" value={formatMoney(resaleUnit.sellerNetPayout)} />
                </div>

                <hr />
                <h6 className="mb-3">Documents</h6>
                <DocumentsList title="Installment paper" documents={installmentPapers} />
                <DocumentsList title="Purchase documents" documents={purchaseDocuments} />
                {otherDocuments.length > 0 && <DocumentsList title="Other documents" documents={otherDocuments} />}
                <div className="mb-3">
                    <strong>Documents verified:</strong> {resaleUnit.documentsVerified ? 'Yes' : 'Not yet'}
                </div>
                <div className="mb-3">
                    <strong>No over-price acknowledged by seller:</strong>{' '}
                    {resaleUnit.noOverPriceAcknowledged ? 'Yes' : 'No'}
                </div>

                <hr />
                <h6 className="mb-3">Media</h6>
                <div className="d-flex flex-wrap gap-2">
                    {resaleUnit.picture && (
                        <a href={resaleUnit.picture} target="_blank" rel="noopener noreferrer">
                            <img
                                src={resaleUnit.picture}
                                alt="Resale unit"
                                style={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 8 }}
                            />
                        </a>
                    )}
                    {gallery.map((image, index) => (
                        <a key={index} href={image} target="_blank" rel="noopener noreferrer">
                            <img
                                src={image}
                                alt={`Gallery ${index + 1}`}
                                style={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 8 }}
                            />
                        </a>
                    ))}
                    {!resaleUnit.picture && gallery.length === 0 && <span>No images uploaded.</span>}
                </div>

                {resaleUnit.notes && (
                    <>
                        <hr />
                        <h6 className="mb-3">Seller notes</h6>
                        <p className="mb-0">{resaleUnit.notes}</p>
                    </>
                )}

                {resaleUnit.status === 'rejected' && resaleUnit.rejectionReason && (
                    <>
                        <hr />
                        <h6 className="mb-2 text-danger">Rejection reason</h6>
                        <p className="mb-0">{resaleUnit.rejectionReason}</p>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewResaleUnitModal;
